// Best-of-N scoring engine. A match is a series of games whose individual point
// scores live in `Cell.games` / `BracketNode.games`. Everything else — games
// won, total points, lifecycle status and the winner — is derived here, so the
// per-game array stays the single source of truth.

import type { GameScore, MatchStatus } from './types';

/** Games a player must win to take a best-of-`bestOf` series. */
export function gamesToWin(bestOf: number): number {
  return Math.floor(bestOf / 2) + 1;
}

export interface MatchSummary {
  gamesA: number; // games won by side A
  gamesB: number; // games won by side B
  pointsA: number; // total points scored by A (secondary tie-breaker)
  pointsB: number; // total points scored by B
  winner: 'A' | 'B' | null; // set only once a side clinches the series
  complete: boolean; // a side has reached gamesToWin
}

/** Aggregates a match's per-game scores into games won, points and a winner. */
export function summarizeMatch(
  games: readonly GameScore[],
  bestOf: number,
): MatchSummary {
  let gamesA = 0;
  let gamesB = 0;
  let pointsA = 0;
  let pointsB = 0;
  for (const game of games) {
    pointsA += game.pointsA;
    pointsB += game.pointsB;
    if (game.pointsA > game.pointsB) gamesA += 1;
    else if (game.pointsB > game.pointsA) gamesB += 1;
  }
  const need = gamesToWin(bestOf);
  const winner = gamesA >= need ? 'A' : gamesB >= need ? 'B' : null;
  return { gamesA, gamesB, pointsA, pointsB, winner, complete: winner !== null };
}

/** Derived lifecycle status from the recorded games. */
export function deriveStatus(
  games: readonly GameScore[],
  bestOf: number,
): MatchStatus {
  if (games.length === 0) return 'PENDING';
  return summarizeMatch(games, bestOf).complete ? 'COMPLETED' : 'IN_PROGRESS';
}

/** Stable code identifying why a series is invalid (for localised messages). */
export type GamesInvalidCode =
  | 'TIE'
  | 'TOO_MANY_WINS'
  | 'BOTH_WINNERS'
  | 'AFTER_DECIDED';

export interface GamesValidation {
  valid: boolean;
  /** English description of the violation (server responses, logs). */
  reason?: string;
  /** Machine-readable violation code; the UI renders it via the catalogue. */
  code?: GamesInvalidCode;
  /** Interpolation params for the localised message keyed by `code`. */
  params?: Record<string, number>;
}

/**
 * Enforces best-of rules. A valid series is a legal prefix of a real match:
 *   - every recorded game has a winner (no ties);
 *   - neither side exceeds the games-to-win threshold, and they can't both
 *     reach it;
 *   - no games are recorded after the series is already clinched (the final
 *     game of a complete series must be won by the series winner).
 */
export function validateGames(
  games: readonly GameScore[],
  bestOf: number,
): GamesValidation {
  const need = gamesToWin(bestOf);
  let gamesA = 0;
  let gamesB = 0;
  for (const game of games) {
    if (game.pointsA === game.pointsB) {
      return {
        valid: false,
        reason: 'Each game needs a winner (no ties).',
        code: 'TIE',
      };
    }
    if (game.pointsA > game.pointsB) gamesA += 1;
    else gamesB += 1;
  }
  if (gamesA > need || gamesB > need) {
    return {
      valid: false,
      reason: `Best of ${bestOf}: a player can win at most ${need} games.`,
      code: 'TOO_MANY_WINS',
      params: { bestOf, need },
    };
  }
  if (gamesA === need && gamesB === need) {
    return {
      valid: false,
      reason: 'Both players cannot win the series.',
      code: 'BOTH_WINNERS',
    };
  }
  if (gamesA === need || gamesB === need) {
    const last = games[games.length - 1];
    const clinchedByA = gamesA === need;
    const lastWonByA = last.pointsA > last.pointsB;
    if (lastWonByA !== clinchedByA) {
      return {
        valid: false,
        reason: 'No games can be played after the series is decided.',
        code: 'AFTER_DECIDED',
      };
    }
  }
  return { valid: true };
}

/** Coerces a value to an odd positive integer, falling back when invalid. */
export function normalizeBestOf(
  value: number | undefined,
  fallback: number,
): number {
  if (
    value === undefined ||
    !Number.isInteger(value) ||
    value < 1 ||
    value % 2 === 0
  ) {
    return fallback;
  }
  return value;
}
